/**
 * ChatMessage 组件
 * 以 ProcessStep 为中心的消息渲染组件，优先展示流式处理过程
 *
 * 设计理念：
 * 1. 优先渲染 processLog 中的内容，实时展示AI处理过程
 * 2. ProcessStep 默认展开，提供类似"思考过程"的体验
 * 3. 当 message.content 存在时，突出显示最终结果
 * 4. 支持 ProcessStep 列表的折叠/展开控制（组件内部状态）
 * 5. 使用 CSS transition 解决 ID 变化时的跳动问题
 * 6. 只有 hasContent 时才显示折叠按钮
 */
import React, { memo, useMemo, useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';


import { Message } from '../../../../types';
import { ProcessSteps } from './ProcessSteps';

import { messageOpacityAtom } from '../../../../atoms/v3/chatPersonalizationAtoms';
import { isDarkAtom } from '../../../../atoms/shared/uiAtoms';
import { selectedCharacterAtom } from '../../../../atoms/v3/characterAtoms';
import { useCharacterById } from '../../../../hooks/v3/chat';


import { TempoProtocolLink } from '../../../../components/v3/TempoProtocolLink';

import { TEMPO_PROTOCOL_NAME, extractTempoTaskLinksFromText } from '../../../../constants/tempoProtocol';
import { MessageTaskReferences } from '../chat/MessageTaskReferences';
import { UserContent } from './UserContent';
import { MessageBottomBar } from './MessageBottomBar';

interface ChatMessageProps {
  message: Message & { tempKey?: string };
  onToggleTask?: (taskId: string, completed: boolean) => void;
  onTaskClick?: (taskId: string) => void;
  forceTheme?: 'light' | 'dark'; // 强制指定主题，用于预览
}



const ChatMessageComponent: React.FC<ChatMessageProps> = ({
  message,
  onToggleTask,
  onTaskClick,
  forceTheme
}) => {
  const messageOpacity = useAtomValue(messageOpacityAtom);

  // 获取当前主题状态
  const isDark = useAtomValue(isDarkAtom);

  // 获取当前选中的角色信息（作为回退）
  const selectedCharacter = useAtomValue(selectedCharacterAtom);

  // 获取消息对应的角色卡信息
  const messageCharacterId = message.characterId || message.metadata?.characterId;
  const { character: messageCharacter, isLoading: isCharacterLoading } = useCharacterById(messageCharacterId);

  // 确定要显示的角色卡：优先使用消息的角色卡，回退到当前选中的角色卡
  const displayCharacter = messageCharacter || selectedCharacter;

  // 淡入效果状态 - 确实有用！
  const [isVisible, setIsVisible] = useState(false);

  // ProcessStep 折叠展开状态 - 组件内部状态
  const [isProcessStepExpanded, setIsProcessStepExpanded] = useState(true);

  // 组件挂载时淡入
  useEffect(() => {
    setIsVisible(true);
  }, []);

  const isUser = message.role === 'user';
  // 检查消息是否为错误状态
  const isError = message.status === 'error';
  // 对于助手消息，检查是否有内容
  const hasContent = !isUser && Boolean(message.content && (
    (typeof message.content === 'string' && message.content.length > 0) ||
    (Array.isArray(message.content) && message.content.length > 0)
  ));

  // 检查是否有 processLog 数据
  const hasProcessStep = Boolean(message.processStep && message.processStep.length > 0);

  // 处理 ProcessStep 折叠展开
  const handleToggleProcessStep = () => {
    setIsProcessStepExpanded(prev => !prev);
  };

  // 简化版本：暂时移除复杂的位置锁定逻辑
  

  // 计算AI消息的背景样式
  const getAIMessageBackgroundStyle = useMemo(() => {
    if (isUser) return {};

    const opacity = messageOpacity / 100;
    let isDarkMode: boolean;
    if (forceTheme) {
      isDarkMode = forceTheme === 'dark';
    } else {
      isDarkMode = isDark;
    }

    // 错误状态使用红色背景
    if (isError) {
      if (isDarkMode) {
        return {
          backgroundColor: `rgba(220, 38, 38, ${opacity * 0.2})`, // red-600 with reduced opacity
        } as React.CSSProperties;
      } else {
        return {
          backgroundColor: `rgba(254, 226, 226, ${opacity})`, // red-100
        } as React.CSSProperties;
      }
    }

    if (isDarkMode) {
      return {
        backgroundColor: `rgba(58, 58, 60, ${opacity})`,
      } as React.CSSProperties;
    } else {
      if (opacity === 1) {
        return {
          backgroundColor: `rgba(209, 209, 214, 1)`,
        } as React.CSSProperties;
      } else {
        return {
          backgroundColor: `rgba(255, 255, 255, ${opacity})`,
        } as React.CSSProperties;
      }
    }
  }, [isUser, messageOpacity, forceTheme, isDark, isError]);

  // 计算AI消息的文本颜色
  const getAIMessageTextColor = useMemo(() => {
    if (isUser) return '';

    let isDarkMode: boolean;
    if (forceTheme) {
      isDarkMode = forceTheme === 'dark';
    } else {
      isDarkMode = isDark;
    }

    // 错误状态使用红色文本
    if (isError) {
      return isDarkMode ? 'text-red-300' : 'text-red-800';
    }

    return isDarkMode ? 'text-white' : 'text-ios-black';
  }, [isUser, forceTheme, isDark, isError]);

  // 计算头像背景色
  const getAvatarBackgroundColor = useMemo(() => {
    let isDarkMode: boolean;
    if (forceTheme) {
      isDarkMode = forceTheme === 'dark';
    } else {
      isDarkMode = isDark;
    }

    if (isUser) {
      return isDarkMode ? 'bg-ios-dark-gray3' : 'bg-ios-gray3';
    } else {
      return isDarkMode ? 'bg-ios-dark-blue' : 'bg-ios-blue';
    }
  }, [isUser, forceTheme, isDark]);



  // 计算markdown prose样式
  const getProseClasses = useMemo(() => {
    let isDarkMode: boolean;
    if (forceTheme) {
      isDarkMode = forceTheme === 'dark';
    } else {
      isDarkMode = isDark;
    }

    return isDarkMode ? 'prose prose-invert prose-sm max-w-none message-markdown' : 'prose prose-sm max-w-none message-markdown';
  }, [forceTheme, isDark]);

  // 移除旧的全局状态管理逻辑，现在使用组件内部状态

  // 提取消息中的任务引用
  const taskReferences = useMemo(() => {
    let messageText = '';

    if (typeof message.content === 'string') {
      messageText = message.content;
    } else if (Array.isArray(message.content)) {
      const textContent = message.content.find(item => item.type === 'text');
      if (textContent && typeof textContent.text === 'string') {
        messageText = textContent.text;
      }
    }

    return extractTempoTaskLinksFromText(messageText);
  }, [message.content]);



  // 渲染AI消息内容 - 统一的ReactMarkdown渲染逻辑
  const renderAIContent = (content: string) => {
    return (
      <div className={getProseClasses}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          urlTransform={(url) => {
            if (url.startsWith(TEMPO_PROTOCOL_NAME + '://')) {
              return url;
            }
            return url;
          }}
          components={{
            a: ({ href, children, ...props }) => {
              if (href?.startsWith(TEMPO_PROTOCOL_NAME + '://')) {
                return <TempoProtocolLink href={href}>{children}</TempoProtocolLink>;
              }
              return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
            }
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    );
  };

  // 简化版本：只使用基本的 CSS transition

  return (
    <div
      className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} mb-3`}
      style={{
        opacity: isVisible ? 1 : 0,
      }}
    >
      {/* 用户消息渲染 - 使用 UserContent 组件 */}
      {isUser && (
        <UserContent
          message={message}
          getAvatarBackgroundColor={getAvatarBackgroundColor}
        />
      )}

      {/* AI消息渲染 - 头像区 + 内容区布局 */}
      {!isUser && (
        <div className="flex justify-start w-full">
          {/* 头像区 */}
          <div className={`w-8 h-8 rounded-full ${getAvatarBackgroundColor} flex items-center justify-center text-white text-xs font-semibold mr-3 flex-shrink-0 mt-1 overflow-hidden ${isCharacterLoading ? 'opacity-50' : ''}`}>
            {isCharacterLoading ? (
              // 加载状态显示占位符
              <div className="w-full h-full rounded-full bg-gray-300 dark:bg-gray-600 animate-pulse" />
            ) : displayCharacter?.avatarUrl ? (
              <img
                src={displayCharacter.avatarUrl}
                alt={displayCharacter.name || 'AI'}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              displayCharacter?.name?.charAt(0).toUpperCase() || 'AI'
            )}
          </div>

          {/* 内容区 - 统一背景色块 */}
          <div className={`flex-1 ${isError ? '' : 'max-w-[75%]'}`}>
            <div
              className={`relative px-3 py-3 rounded-2xl shadow-sm text-sm ${getAIMessageTextColor} rounded-bl-none border-l-4 ${
                isError
                  ? 'border-red-500 dark:border-red-400 bg-red-50/50 dark:bg-red-900/10'
                  : 'border-ios-blue dark:border-ios-dark-blue'
              } ${isError ? 'w-fit' : ''}`}
              style={isError ? undefined : getAIMessageBackgroundStyle}
            >
              {/* ProcessSteps 区域 - 只要hasProcessLog就渲染 */}
              {hasProcessStep && (
                <div className="mb-3">
                  <ProcessSteps
                    processLog={message.processStep || []}
                    isExpanded={isProcessStepExpanded}
                    onToggle={handleToggleProcessStep}
                    showToggleButton={hasContent} // 只有hasContent时才显示折叠按钮
                    forceTheme={forceTheme}
                  />
                </div>
              )}

              {/* 最终内容区域 */}
              {hasContent && (
                <div className="mb-3">
                  {/* 内容主体 */}
                  {(() => {
                    if (Array.isArray(message.content)) {
                      const textContentItem = message.content.find(item => item.type === 'text');
                      if (textContentItem && typeof textContentItem.text === 'string') {
                        return renderAIContent(textContentItem.text);
                      }
                    } else if (typeof message.content === 'string') {
                      return renderAIContent(message.content);
                    }
                    return null;
                  })()}
                </div>
              )}

              {/* 底栏组件 - Logo、复制按钮和测试按钮 */}
              <MessageBottomBar
                message={message}
                hasContent={hasContent}
                hasProcessStep={hasProcessStep}
                messageContent={(() => {
                  if (Array.isArray(message.content)) {
                    const textContentItem = message.content.find(item => item.type === 'text');
                    return textContentItem?.text || '';
                  } else if (typeof message.content === 'string') {
                    return message.content;
                  }
                  return '';
                })()}
              />
            </div>

            {/* 显示消息中提到的任务 - 在统一背景外 */}
            {taskReferences.length > 0 && onToggleTask && onTaskClick && (
              <div className="w-full mt-2">
                <MessageTaskReferences
                  taskReferences={taskReferences}
                  onToggleTask={onToggleTask}
                  onTaskClick={onTaskClick}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// 使用自定义比较函数的 memo
export const ChatMessage = memo(ChatMessageComponent, (prevProps, nextProps) => {
  if (prevProps.message.id === nextProps.message.id) {
    const prevContentUpdateTime = prevProps.message.contentUpdateTime
    const nextContentUpdateTime = nextProps.message.contentUpdateTime

    if(!prevContentUpdateTime && !nextContentUpdateTime) {
      return true;
    }
    if(!prevContentUpdateTime || !nextContentUpdateTime) {
      return false;
    }

    return nextContentUpdateTime === prevContentUpdateTime;
  }

  return prevProps.message === nextProps.message;
});

export default ChatMessage;
